import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { API_BASE, chLabel, chColor, CONSENT, nf, money } from './shared';

const STEPS = [
  ['visit', 'Ziyaret'], ['view_item', 'Ürün görüntüleme'], ['add_to_cart', 'Sepete ekleme'], ['begin_checkout', 'Ödemeye başlama'], ['purchase', 'Satın alma'],
];
const rate = (a, b) => (b > 0 ? `%${((a / b) * 100).toFixed(1).replace('.', ',')}` : '-');

function FunnelTable({ rows, keyName, labelOf, dotOf }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-gray-500 border-b border-gray-200">
            <th className="py-2 pr-3 font-medium">{keyName}</th>
            {STEPS.map(([k, l]) => <th key={k} className="py-2 pr-3 font-medium text-right whitespace-nowrap">{l}</th>)}
            <th className="py-2 pr-3 font-medium text-right whitespace-nowrap">Ziyaret → Satın alma</th>
            <th className="py-2 font-medium text-right">Satın alma değeri</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.__k} className="border-b border-gray-100 last:border-0">
              <td className="py-2 pr-3"><span className="inline-flex items-center gap-1.5 font-medium text-gray-900">{dotOf && <span className="w-2 h-2 rounded-full" style={{ background: dotOf(r.__k) }} />}{labelOf(r.__k)}</span></td>
              {STEPS.map(([k], i) => (
                <td key={k} className="py-2 pr-3 text-right tabular-nums" title={r.__raw ? `Açık sayı: ${nf.format(r.__raw[k] || 0)}` : undefined}>
                  {nf.format(r[k] || 0)}
                  {k === 'purchase' && r.purchase_cancelled > 0 && <div className="text-[10px] text-gray-400">iptal: {nf.format(r.purchase_cancelled)}</div>}
                  {i > 0 && <div className="text-[10px] text-gray-400">{rate(r[k] || 0, r[STEPS[i - 1][0]] || 0)}</div>}
                </td>
              ))}
              <td className="py-2 pr-3 text-right tabular-nums font-medium">{rate(r.purchase || 0, r.visit || 0)}</td>
              <td className="py-2 text-right tabular-nums">{money(r.purchase_value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function FunnelSection({ from, to, includeTest }) {
  const brandId = useAuth().selectedBrand?.id;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [raw, setRaw] = useState(false);
  useEffect(() => {
    if (!brandId) return undefined;
    let alive = true;
    setLoading(true); setError('');
    fetch(`${API_BASE}/funnel?brandId=${brandId}&from=${from}&to=${to}&includeTest=${includeTest ? 1 : 0}`, { credentials: 'include' })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((j) => { if (alive) setData(j.data); })
      .catch((e) => { if (alive) setError(e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [brandId, from, to, includeTest]);

  const view = (r0) => (r0 && raw && r0.raw_counts ? { ...r0, ...r0.raw_counts } : r0);
  const total = view(data?.total);
  const max = Math.max(1, total?.visit || 0);
  const byChannel = (data?.byChannel || []).map((r) => ({ ...view(r), __k: r.channel, __raw: r.raw_counts }));
  const byConsent = (data?.byConsent || []).map((r) => ({ ...view(r), __k: r.consent, __raw: r.raw_counts }));

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-medium text-gray-900">Satın alma hunisi</div>
          <p className="text-xs text-gray-500">Her adımda tekil oturum sayısı. Yüzdeler bir önceki adıma göre dönüşümü gösterir.</p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none" title="Her adımda o olayı gören oturum sayısı; oturum bir üst adımı görmemiş olabilir">
            <input type="checkbox" checked={raw} onChange={(e) => setRaw(e.target.checked)} />Açık sayılar
          </label>
          {loading && <Loader2 className="animate-spin text-gray-400" size={16} />}
        </div>
      </div>
      {error && <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-700">Huni alınamadı: {error}</div>}
      {!error && data && !total ? (
        <div className="py-8 text-center text-sm text-gray-500">Bu aralıkta huni olayı yok. Ziyaret ve sepet olayları toplandıkça burada görünür.</div>
      ) : !error && total ? (
        <>
          <div className="space-y-2">
            {STEPS.map(([k, label], i) => {
              const v = total[k] || 0; const prev = i > 0 ? total[STEPS[i - 1][0]] || 0 : null;
              return (
                <div key={k} className="flex flex-wrap sm:flex-nowrap items-center gap-x-3 gap-y-1">
                  <div className="order-1 flex-1 sm:flex-none sm:w-40 text-xs text-gray-700">{label}</div>
                  <div className="order-3 sm:order-2 w-full sm:w-auto sm:flex-1 h-7 bg-gray-100 rounded relative overflow-hidden">
                    <div className="h-full rounded" style={{ width: `${Math.max(v > 0 ? 1.5 : 0, (v / max) * 100)}%`, background: i === STEPS.length - 1 ? '#059669' : '#4f46e5', opacity: 1 - i * 0.12 }} />
                  </div>
                  <div className="order-2 sm:order-3 sm:w-32 text-right text-xs tabular-nums flex-shrink-0">
                    <span className="font-semibold text-gray-900">{nf.format(v)}</span>
                    {prev !== null && <span className="text-gray-400"> · {rate(v, prev)}</span>}
                    {k === 'purchase' && total.purchase_cancelled > 0 && <span className="text-gray-400 font-normal"> · iptal: {nf.format(total.purchase_cancelled)}</span>}
                  </div>
                </div>
              );
            })}
          </div>
          <div>
            <div className="text-xs font-medium text-gray-700 mb-1">Kanal bazında</div>
            <FunnelTable rows={byChannel} keyName="Kanal" labelOf={chLabel} dotOf={chColor} />
          </div>
          <div>
            <div className="text-xs font-medium text-gray-700 mb-1">İzin durumuna göre</div>
            <FunnelTable rows={byConsent} keyName="İzin" labelOf={(c) => CONSENT[c]?.label || c} />
          </div>
          <p className="text-[11px] text-gray-500">
            Huni, her oturumun ulaştığı en derin adıma göre sayılır. Kanal ve izin, oturum başına tek değerdir. Satın alma yalnızca geçerli siparişleri sayar; iptal/iade edilen siparişin oturumu ödemeye başlama adımında kalır.{raw ? ' Şu an açık sayılar gösteriliyor: her adımda o olayı gören oturumlar, bir üst adım görülmemiş olabilir.' : ''}
          </p>
        </>
      ) : null}
    </div>
  );
}
